import { jsPDF } from 'jspdf';
import { BrandingConfig } from '../services/api/brandingService';
import { tenantBranding } from '../services/config';

/** Marque par défaut pour métadonnées PDF et libellés optionnels. */
export const FAMLA_EXPORT_BRAND = `Made with ${tenantBranding.appName}`;
export const FAMLA_EXPORT_PRODUCER = tenantBranding.appName;

const FAMLA_LOGO_SRC: string = require('../assets/img/famla.png');

let logoLoadPromise: Promise<HTMLImageElement> | null = null;
let customLogoCache: Map<string, Promise<HTMLImageElement>> = new Map();

/** Précharge le logo par défaut (cache singleton). */
export function preloadFamlaExportLogo(): Promise<HTMLImageElement> {
    if (!logoLoadPromise) {
        logoLoadPromise = new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Failed to load default export logo'));
            img.src = FAMLA_LOGO_SRC;
        });
    }
    return logoLoadPromise;
}

/** Précharge un logo personnalisé (cache par URL). */
export function preloadCustomExportLogo(logoUrl: string): Promise<HTMLImageElement> {
    if (customLogoCache.has(logoUrl)) {
        return customLogoCache.get(logoUrl)!;
    }
    
    const promise = new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Permettre le cross-origin pour les logos externes
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load custom export logo: ${logoUrl}`));
        img.src = logoUrl;
    });
    
    customLogoCache.set(logoUrl, promise);
    return promise;
}

/** Récupère le texte de branding (custom ou défaut). */
export function getExportBrandText(branding?: BrandingConfig): string {
    if (branding?.enabled && branding?.export_brand_text) {
        return branding.export_brand_text;
    }
    return FAMLA_EXPORT_BRAND;
}

/** Récupère le nom du producteur (custom ou défaut). */
export function getExportProducer(branding?: BrandingConfig): string {
    if (branding?.enabled && branding?.app_name) {
        return branding.app_name;
    }
    return FAMLA_EXPORT_PRODUCER;
}

export type ExtendCanvasFamlaBrandingOptions = {
    /** Multiplie la taille du logo (défaut 1). */
    brandingScale?: number;
    /** Configuration de branding personnalisé (optionnel). */
    branding?: BrandingConfig;
};

/**
 * Logo en coin bas-droit (style Gemini), sans bandeau.
 * Utilise le logo personnalisé si branding.enabled, sinon logo par défaut.
 * Utilisé pour exports PNG / PDF cartographie (html2canvas).
 */
export async function extendCanvasWithFamlaBranding(
    source: HTMLCanvasElement,
    options?: ExtendCanvasFamlaBrandingOptions,
): Promise<HTMLCanvasElement> {
    const scale = Math.max(0.75, options?.brandingScale ?? 1);
    const out = document.createElement('canvas');
    out.width = source.width;
    out.height = source.height;
    const ctx = out.getContext('2d');
    if (!ctx) {
        return source;
    }

    ctx.drawImage(source, 0, 0);

    try {
        // [WHITE LABELLING] Utiliser le logo personnalisé si disponible
        let logo: HTMLImageElement;
        const branding = options?.branding;
        
        if (branding?.enabled && (branding?.export_logo_url || branding?.logo_url)) {
            const customLogoUrl = branding.export_logo_url || branding.logo_url;
            logo = await preloadCustomExportLogo(customLogoUrl!);
        } else if (tenantBranding.enabled && (tenantBranding.logoUrl || tenantBranding.logoLightUrl)) {
            const tenantLogoUrl = tenantBranding.logoUrl || tenantBranding.logoLightUrl;
            logo = await preloadCustomExportLogo(tenantLogoUrl!);
        } else {
            logo = await preloadFamlaExportLogo();
        }
        
        const logoNatW = logo.naturalWidth || logo.width;
        const logoNatH = logo.naturalHeight || logo.height;
        const aspect = logoNatW / logoNatH;

        const logoH = Math.max(26, Math.min(64, Math.round(source.width * 0.034 * scale)));
        const logoW = Math.round(logoH * aspect);
        const edgePad = Math.max(14, Math.round(source.width * 0.014));
        const logoX = source.width - edgePad - logoW;
        const logoY = source.height - edgePad - logoH;

        ctx.drawImage(logo, logoX, logoY, logoW, logoH);
    } catch {
        // Logo indisponible : retourner l'image source sans filigrane
    }

    return out;
}

/** Métadonnées PDF + libellé coin bas-droit (exports texte, ex. guide collecte). */
export function stampFamlaBrandingOnJsPdf(doc: jsPDF, title?: string, branding?: BrandingConfig): void {
    const producer = getExportProducer(branding);
    const brandText = getExportBrandText(branding);
    const appName = branding?.enabled && branding?.app_name ? branding.app_name : tenantBranding.appName;
    
    doc.setProperties({
        title: title?.trim() || `${appName} export`,
        creator: producer,
        subject: brandText,
    });

    const total = doc.getNumberOfPages();
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 12;

    for (let i = 1; i <= total; i += 1) {
        doc.setPage(i);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(brandText, pageW - margin, pageH - margin, { align: 'right' });
    }
    doc.setPage(total);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
}

/** Métadonnées sur PDF image-only (cartographie). */
export function stampFamlaPdfImageMetadata(pdf: jsPDF, title?: string, branding?: BrandingConfig): void {
    const producer = getExportProducer(branding);
    const brandText = getExportBrandText(branding);
    
    pdf.setProperties({
        title: title?.trim() || 'Process map',
        creator: producer,
        subject: brandText,
    });
}
